<template>
    <div>
        <loader v-if="isLoading" />
        <template v-if="!isLoading && isCanDelete">
            <div v-if="isSelected" class="cursor-pointer pl-2 py-2">
                <b @click="bulkDelete()" class="text-primary cursor-pointer"
                    >Delete Selected
                </b>
                |
                <b @click="unselectAll()" class="text-primary cursor-pointer"
                    >Unselect All
                </b>
                <span
                    v-if="isSelected"
                    class="text-muted text-center mb-0 p-2 mx-2"
                >
                    (
                    {{ selectLength }}
                    {{ selectLength == 1 ? "item" : "items" }} selected )
                </span>
            </div>
            <div v-else class="pl-2 py-2">
                <span class="text-muted cursor-not-allowed"
                    >Delete Selected</span
                >
                |
                <span class="text-muted cursor-not-allowed">Unselect All</span>
            </div>
        </template>
        <b-table-simple
            v-if="!isLoading"
            borderless
            hover
            class="list"
            id="table-letters"
        >
            <b-thead>
                <b-tr>
                    <b-th v-if="isCanDelete" style="width: 1%;"></b-th>
                    <b-th>Title:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Type:</b-th>
                    <b-th>Date:</b-th>
                    <b-th>Created By:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(doc, i) in documents" :key="i">
                    <td v-if="isCanDelete">
                        <b-form-checkbox
                            v-if="_hasAccess('patient.record.delete')"
                            v-model="doc.deleteItem"
                            @change="checkboxChange"
                            plain
                        >
                        </b-form-checkbox>
                    </td>
                    <b-td>
                        <input type="hidden" name="id" :value="doc.id" />
                        <a :href="doc.s3_link" target="_blank">
                            {{ doc.title || "Certificate" }}</a
                        >
                    </b-td>

                    <b-td>
                        <client-name
                            v-if="doc.patient.primary_owner.length > 0"
                            :client="doc.patient.primary_owner[0]"
                            :text="doc.patient.primary_owner[0].name"
                        />
                    </b-td>

                    <b-td>
                        {{ doc.cert_type }}
                    </b-td>

                    <b-td>
                        {{ _carbonDateDisplay(doc.create_at) }}
                    </b-td>

                    <b-td>
                        {{ doc.create_by ? doc.create_by.label : "-" }}
                    </b-td>

                    <b-td class="options">
                        <b-dropdown
                            v-if="
                                _hasAccess('patient.record.update') ||
                                _hasAccess('patient.record.delete')
                            "
                            key=""
                            size="sm"
                            variant="link"
                            right
                            no-caret
                            lazy
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                v-if="_hasAccess('patient.certificate.email')"
                                @click="emailCertificate(doc)"
                            >
                                <i class="cus-email"></i> Email Certificate
                            </b-dropdown-item>

                            <template
                                v-if="
                                    _hasAccess('patient.certificate.sms') &&
                                    (_isExtensionEnabled(
                                        EXTENSIONS.CODE.CODE_SMS
                                    ) ||
                                        _isExtensionEnabled(
                                            EXTENSIONS.CODE.CODE_2_WAY_MESSAGING
                                        ))
                                "
                            >
                                <b-dropdown-item
                                    v-if="isClientNumberExists(doc.patient)"
                                    @click="smsCertificate(doc)"
                                >
                                    <i class="cus-phone"></i> Text (SMS)
                                    Certificate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-else
                                    disabled
                                    v-b-tooltip.hover="{
                                        placement: 'left',
                                        title:
                                            CCLIENT.ALERT_MESSAGE
                                                .MOBILE_NOT_EXIST
                                    }"
                                    @click="smsCertificate(doc)"
                                >
                                    <i class="cus-phone"></i> Text (SMS)
                                    Certificate
                                </b-dropdown-item>
                            </template>
                            <b-dropdown-item
                                v-if="_hasAccess('patient.certificate.print')"
                                @click="printCertificate(doc.s3_link)"
                            >
                                <i class="cus-printer"></i> Print Certificate
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </b-tr>
                <b-tr v-if="documents.length === 0">
                    <b-td colspan="6">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="showPagination"
            class="vet-pagination"
            align="right"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="changePage"
        />

        <modal-certificate-send-sms
            :module="COMMUNICATION.MODULE_CLIENT"
            ref="modal-certificate-send-sms"
        />
        <modal-email-certificate ref="modal-email-certificate" />

        <modal-delete-in-bulk
            ref="modalBulkDelete"
            @onSuccess="onSuccess"
            :documents="documents"
            :currentPage="currentPage"
        />
    </div>
</template>

<script>
import { forEach, filter } from "lodash";
import CCLIENT from "@/constants/clients";
import EXTENSIONS from "@/constants/extensions";
import Loader from "@/components/elements/loader-list";
import COMMUNICATION from "@/constants/communications";
import ClientName from "@/components/commons/client-name";
import PatientDocument from "@/services/patient-documents";
import ModalEmailCertificate from "@/components/certificates/document-email-certificate-modal.vue";
import ModalCertificateSendSms from "@/components/certificates/modal-certificate-send-sms.vue";

export default {
    components: {
        Loader,
        ClientName,
        ModalCertificateSendSms,
        ModalEmailCertificate,
        ModalDeleteInBulk: () =>
            import(
                "@/components/certificates/modal-bulk-delete-certificates.vue"
            )
    },
    props: {
        patientId: {
            type: String
        }
    },
    data() {
        return {
            documents: [],
            isLoading: false,
            clientId: this.$store.state.client.client_id,
            isLoadingPrintRecordId: false,
            currentPage: 1,
            totalRows: 0,
            perPage: 0,
            showPagination: false,
            CI_APPS: process.env.CI_APPS,
            COMMUNICATION: COMMUNICATION,
            CCLIENT: CCLIENT,
            isDeleteMultiple: false,
            isSelected: false,
            selectLength: 0,
            EXTENSIONS: EXTENSIONS
        };
    },
    computed: {
        userId() {
            return this.$route.params.id;
        },
        isCanDelete() {
            return this._hasAccess("patient.record.delete");
        }
    },
    mounted() {
        this.getCertificates(1);
    },
    methods: {
        async getCertificates(page) {
            try {
                this.isLoading = true;

                const res = await PatientDocument.getCertificates({
                    type: "patient",
                    page: page,
                    patientId: this.patientId
                });

                if (res.code && res.code === 200) {
                    this.documents = res.resource.data;
                }

                this.isLoading = false;

                // pagination
                this.currentPage = res.resource.current_page;
                this.totalRows = res.resource.total;
                this.perPage = res.resource.per_page;
                this.showPagination = res.resource.last_page > 1;
                this.checkboxChange();
            } catch (e) {
                console.log(e);
            }
        },
        emailCertificate(document) {
            this.$refs["modal-email-certificate"].open(document);
        },
        smsCertificate(document) {
            let copiedDocument = { ...document };
            if (copiedDocument.patient.primary_owner) {
                let primaryOwner = copiedDocument.patient.primary_owner;
                copiedDocument.client =
                    primaryOwner.length == 1 ? primaryOwner[0] : {};
            }

            this.$refs["modal-certificate-send-sms"].open(copiedDocument);
        },
        printCertificate(document) {
            if (document) {
                // eslint-disable-next-line no-undef
                window.open(document, "_blank");
            } else {
                this._message("Error: Unable to print", "danger");
            }
        },
        changePage(nextPage) {
            this.getCertificates(nextPage);
        },
        isClientNumberExists(patient) {
            if (patient.primary_owner.length > 0) {
                return patient.primary_owner[0].mobile;
            }
            return false;
        },
        checkboxChange() {
            this.$nextTick(() => {
                let listSelected = filter(this.documents, "deleteItem");
                this.selectLength = listSelected.length;
                this.isSelected = this.selectLength > 0;
            });
        },
        unselectAll() {
            forEach(this.documents, function (doc) {
                doc.deleteItem = false;
            });

            this.checkboxChange();
        },
        bulkDelete() {
            this.$refs.modalBulkDelete.open();
        },
        onSuccess() {
            this.$emit("getTabCounts");
            this.getCertificates(this.currentPage);
        }
    }
};
</script>

<style scoped>
td.options {
    width: 50px;
    text-align: right;
}
.vet-pagination {
    margin-top: 50px;
}
</style>
